/**
 * Vault — formulario del censo para el dueño propuesto (F6). Self-scoped: cada
 * persona ve solo las cuentas que IT le asignó y responde las 4 preguntas clave.
 * No exige acceso al Vault ni passphrase: puede ser alguien que nunca lo usó.
 *
 *   /vault/census                 mis cuentas a relevar
 *   /vault/census/:accountEmail   el formulario de una
 */
import { useEffect, useState } from 'react';
import { useVaultLabels } from '@/components/vault/vault-ui';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EmptyState, LoaderBlock } from '@/components/internal/shared';
import { errMsg } from '@/hooks/useVault';
import { CENSUS_QUESTIONS, type CensusQuestionKey, type VaultSharedAccount } from '../../../../shared/types/vault';

const MFA_UNKNOWN = '__unknown__';

export function CensusFormPage() {
  const { t } = useTranslation();
  const { accountEmail: raw } = useParams();
  const accountEmail = raw ? decodeURIComponent(raw).trim().toLowerCase() : null;
  const mine = trpc.vault.getMyCensusAssignments.useQuery(undefined, { staleTime: 30_000 });

  if (mine.isLoading) return <LoaderBlock />;
  const rows = mine.data ?? [];

  if (accountEmail) {
    const account = rows.find((r) => r.accountEmail === accountEmail);
    if (!account) {
      return (
        <div className="mx-auto max-w-3xl p-2 sm:p-4">
          <EmptyState
            title={t('vault.census.not_assigned', 'Esta cuenta no te fue asignada')}
            description={t('vault.census.not_assigned_body', 'Si creés que es un error, escribile a IT.')}
          />
        </div>
      );
    }
    return <CensusForm account={account} />;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-2 sm:p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-900/30">
          <ClipboardList className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-2xl">{t('vault.census.mine_title', 'Cuentas compartidas a mi cargo')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('vault.census.mine_sub', 'IT te propuso como responsable de estas cuentas. Son 4 preguntas por cuenta, ~3 minutos cada una.')}
          </p>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState title={t('vault.census.mine_empty', 'No tenés cuentas asignadas')} />
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <Card key={r.accountEmail} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <div className="font-mono text-sm">{r.accountEmail}</div>
                {r.probableUse && <div className="text-xs text-muted-foreground">{r.probableUse}</div>}
              </div>
              <div className="flex items-center gap-2">
                {r.answeredAt ? (
                  <Badge variant="success"><CheckCircle2 className="mr-1 h-3 w-3" />{t('vault.census.form_answered', 'Respondido')}</Badge>
                ) : (
                  <Badge variant="secondary">{t('vault.census.pending', 'Pendiente')}</Badge>
                )}
                <Button asChild size="sm" variant={r.answeredAt ? 'outline' : 'default'}>
                  <Link to={`/vault/census/${encodeURIComponent(r.accountEmail)}`}>
                    {r.answeredAt ? t('vault.census.edit_answers', 'Editar respuestas') : t('vault.census.answer', 'Responder')}
                  </Link>
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CensusForm({ account }: { account: VaultSharedAccount }) {
  const { t } = useTranslation();
  const L = useVaultLabels();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [answers, setAnswers] = useState<Partial<Record<CensusQuestionKey, string>>>({});
  const [declaredUse, setDeclaredUse] = useState('');
  const [hasMfa, setHasMfa] = useState<string>(MFA_UNKNOWN);
  const [mfaLocation, setMfaLocation] = useState('');
  const [platforms, setPlatforms] = useState('');
  const [notMine, setNotMine] = useState('');

  useEffect(() => {
    setAnswers(account.answers ?? {});
    setDeclaredUse(account.declaredUse ?? '');
    setHasMfa(account.hasMfa == null ? MFA_UNKNOWN : account.hasMfa ? 'yes' : 'no');
    setMfaLocation(account.mfaLocation ?? '');
    setPlatforms((account.platforms ?? []).join(', '));
    setNotMine(account.ownerNote ?? '');
  }, [account]);

  const submit = trpc.vault.submitCensusForm.useMutation({
    onSuccess: () => {
      utils.vault.getMyCensusAssignments.invalidate();
      toast.success(t('vault.census.submitted', '¡Gracias! Quedó registrado.'));
      navigate('/vault/census');
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const answered = CENSUS_QUESTIONS.filter((q) => (answers[q.key] ?? '').trim()).length;

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-2 sm:p-4">
      <nav className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/vault/census" className="inline-flex items-center gap-1 hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
          {t('vault.census.back', 'Mis cuentas')}
        </Link>
      </nav>
      <div>
        <h1 className="font-display text-2xl">{account.accountEmail}</h1>
        <p className="text-sm text-muted-foreground">
          {t('vault.census.form_sub', 'Queremos saber qué hace esta cuenta hoy para decidir su destino (grupo, accesos individuales, service account o Vault). No hay respuestas incorrectas: "no sé" también sirve.')}
        </p>
        {account.probableUse && (
          <p className="mt-1 text-xs text-muted-foreground">{t('vault.census.hint', 'Uso probable según IT: {{use}}', { use: account.probableUse })}</p>
        )}
      </div>

      <Card className="space-y-4 p-4">
        {CENSUS_QUESTIONS.map((q, i) => (
          <div key={q.key} className="space-y-1">
            <Label>{i + 1}. {L.question(q.key)}</Label>
            <Textarea value={answers[q.key] ?? ''} onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))} rows={2} />
          </div>
        ))}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <Label>{t('vault.census.declared_use', 'Uso declarado')}</Label>
            <Input value={declaredUse} onChange={(e) => setDeclaredUse(e.target.value)} placeholder={t('vault.census.declared_use_ph', 'En una frase: para qué se usa')} />
          </div>
          <div className="space-y-1">
            <Label>{t('vault.census.mfa', '¿Tiene MFA?')}</Label>
            <Select value={hasMfa} onValueChange={setHasMfa}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={MFA_UNKNOWN}>{t('vault.census.mfa_unknown_me', 'No sé')}</SelectItem>
                <SelectItem value="yes">{t('vault.census.mfa_yes', 'Sí')}</SelectItem>
                <SelectItem value="no">{t('vault.census.mfa_no', 'No')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>{t('vault.census.mfa_location', 'Dónde vive el segundo factor')}</Label>
            <Input value={mfaLocation} onChange={(e) => setMfaLocation(e.target.value)} placeholder={t('vault.census.mfa_location_ph2', 'mi teléfono / el de X / no tiene')} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>{t('vault.census.platforms', 'Plataformas que alcanza (separadas por coma)')}</Label>
            <Input value={platforms} onChange={(e) => setPlatforms(e.target.value)} placeholder={t('vault.census.platforms_ph', 'Meta, Google Ads, TikTok')} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>{t('vault.census.not_mine', '¿No es tuya? Decinos de quién es')}</Label>
            <Input value={notMine} onChange={(e) => setNotMine(e.target.value)} placeholder={t('vault.census.not_mine_ph', 'nombre o email de quien la usa')} />
          </div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">{t('vault.census.progress', '{{n}} de {{total}} preguntas respondidas', { n: answered, total: CENSUS_QUESTIONS.length })}</span>
          <Button
            onClick={() =>
              submit.mutate({
                accountEmail: account.accountEmail,
                answers,
                declaredUse: declaredUse || undefined,
                hasMfa: hasMfa === MFA_UNKNOWN ? null : hasMfa === 'yes',
                mfaLocation: mfaLocation || undefined,
                platforms: platforms.split(',').map((p) => p.trim()).filter(Boolean),
                notMineNote: notMine || undefined,
              })
            }
            disabled={submit.isPending || answered === 0}
          >
            {submit.isPending ? t('vault.form.saving', 'Guardando…') : t('vault.census.send', 'Enviar respuestas')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
